import React, { useState, useEffect, useCallback } from 'react'
import { reduxForm } from 'redux-form'
import { useSelector } from 'react-redux'
import { Button } from '@material-ui/core'
import { Col, Row, Card } from 'react-bootstrap'
import {
  Tabs,
  Tab
} from 'components/shared/Tabs'
import FilterBarModule from "components/shared/FilterBarModule"
import TableComponent from 'components/shared/TableComponent'
import { settingsTabs, customerColumns, customerFilters, clientPortalsColumns } from 'constants/roxAdminSettings'
import { ClientForm } from '../DialogForms'
import styles from '../ClientProfile/profile.module.scss'
import strings from 'strings'
import { getClientPortals } from 'actions/client'
import FeatureFlagsTab from "./FeatureFlagsTab";
import {useHistory, useLocation} from "react-router-dom";

const Settings = (props) => {
  const {
    getClientsList,
    handleSubmit,
    reset,
    clientsList,
    createClient,
    resetClientEditForm,
    updateClient,
    getClientById,
    totalRecords,
    totalPages,
    downloadReport,
    getRolesList
  } = props

  const location = useLocation();
  const history = useHistory();
  const [tabValue, setTabValue] = useState(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab === 'client-portals') return 1;
    if (tab === 'feature-flags') return 2;
    return 0;
  });
  const [loading, setLoading] = useState(false)
  const [formLoader, setFormLoader] = useState(false)
  const [modal, setModal] = useState(false)
  const [formMode, setFormMode] = useState(strings.CREATE)

  // Client Portals state
  const [clientPortalsData, setClientPortalsData] = useState([])
  const [portalsLoading, setPortalsLoading] = useState(false)

  const [columns, setColumns] = useState(() => {
    const action = {
      formatter: (cell, row, rowIndex) => (<img src={require('styles/images/icons/tableedit.svg')} onClick={() => handleUserEditForm(row)} className={styles.tableEditIcon} alt='edit' />)
    }
    const cols = customerColumns()
    cols.map((item, index) => {
      if (item.dataField === 'action') {
        cols[index] = { ...item, ...action }
      }
      return item
    })
    return cols
  })
  const { form } = useSelector(state => state);
  const { clientForm: { initial: { client } } } = form || {}

  const getClientsListData = useCallback((filters = {}, pagination = {}) => {
    setLoading(true)
    getClientsList(filters, pagination).finally(() => {
      setLoading(false)
    })
  }, [getClientsList])

  // Fetch client portals data
  const fetchClientPortals = useCallback(() => {
    setPortalsLoading(true)
    getClientPortals()
        .then(data => {
          setClientPortalsData(data || [])
        })
        .finally(() => {
          setPortalsLoading(false)
        })
  }, [])

  useEffect(() => {
    if (!modal) {
      reset('clientForm')
      resetClientEditForm()
    }
  }, [modal, reset, resetClientEditForm])

  useEffect(() => {
    getClientsListData()
    getRolesList()
  }, [getClientsListData, getRolesList])

  // Fetch portals when tab is opened
  useEffect(() => {
    if (tabValue === 1) {
      fetchClientPortals()
    }
  }, [tabValue, fetchClientPortals])

  const handleUserEditForm = (row) => {
    setModal(true)
    setFormMode(strings.EDIT)
    setFormLoader(true)
    getClientById(row?.uuid).finally(() => {
      setFormLoader(false)
    })
  }

  const handlePagination = (pagination) => {
    getClientsListData({}, pagination)
  }

  const handleChange = (e, val) => {
    setTabValue(val);
    const tabNames = ['customers', 'client-portals', 'feature-flags'];
    const params = new URLSearchParams(location.search);
    params.set('tab', tabNames[val]);
    history.replace({ search: params.toString() });
  };

  const handleCustomer = () => {
    setModal(true)
    setFormMode(strings.CREATE)
  }

  const handleClientForm = (formData) => {
    setFormLoader(true)
    const formAction = formMode === strings.EDIT ? updateClient : createClient
    let data = { ...formData }

    if (formMode === strings.EDIT) {
      data = {}
      Object.entries(formData.client).forEach(entries => {
        data[entries[0]] = entries[1]
      })

      if (client.customFCSUsername === formData.client.customFCSUsername)
        delete data.customFCSUsername

      if (client.customFCSPassword === formData.client.customFCSPassword)
        delete data.customFCSPassword
    }

    // Transform featureFlags from object to array for both create and edit
    const flagsSource = formMode === strings.EDIT ? data : data.client;
    if (flagsSource?.featureFlags) {
      flagsSource.featureFlags = Object.entries(flagsSource.featureFlags)
          .filter(([, value]) => value)
          .map(([key]) => key);
    }

    formAction(data)
        .then(() => {
          setFormLoader(false);
          setModal(false);
          getClientsListData();
        })
        .finally(() => {
          setFormLoader(false);
        });
  }

  return (
      <div>
        <Row>
          <Col md={6} className="text-left">
            <h4>{strings.SETTINGS}</h4>
          </Col>
          <Col md={6} className="text-right">
            <Button variant='contained' onClick={downloadReport} color='primary'>
              {strings.DOWNLOAD_REPORT}
            </Button>
          </Col>
        </Row>
        <Tabs
            data={settingsTabs}
            value={tabValue}
            onChange={handleChange}
        />
        <Tab value={tabValue} index={0}>
          <Card>
            <FilterBarModule
                title={strings.ADMIN.CUSTOMER_SETTINGS}
                filters={customerFilters}
                textLabel={'Filters'}
                columns={columns}
                receiveCustomColumn={(columns) => setColumns(columns)}
                filterParams={(params) => getClientsListData(params)}
                btnText={strings.ADD_CUSTOMER}
                onBtnChange={handleCustomer}
                onSearch={(value) => getClientsListData(value ? { clientName: value } : {})}
            />
            <TableComponent
                data={clientsList || []}
                columns={columns}
                sort={true}
                loading={loading}
                totalRecords={totalRecords}
                totalPages={totalPages}
                onChangePagination={(pagination) => handlePagination(pagination)}
            />
          </Card>
        </Tab>
        <Tab value={tabValue} index={1}>
          <Card>
            <FilterBarModule
                title="Client Portals"
            />
            <TableComponent
                data={clientPortalsData}
                columns={clientPortalsColumns}
                keyField="clientUuid"
                sort={true}
                isLoading={portalsLoading}
                useLocalPagination={true}
            />
          </Card>
        </Tab>
        <Tab value={tabValue} index={2}>
          <FeatureFlagsTab />
        </Tab>
        <ClientForm
            open={modal}
            onClose={() => setModal(false)}
            onSubmit={handleSubmit(formData => handleClientForm(formData))}
            mode={formMode}
            loading={formLoader}
            {...props}
        />
      </div>
  )
}

export default reduxForm({
  form: 'clientForm',
  enableReinitialize: true
})(Settings)